interface Feature {
  icon: string;
  title: string;
  description: string;
  color: string;
}

interface WhyChooseUsProps {
  features?: Feature[];
  title?: string;
  subtitle?: string;
}

const defaultFeatures: Feature[] = [
  {
    icon: 'ri-settings-4-line',
    title: 'Fully Equipped Studios',
    description: 'Every studio comes stocked with professional-grade lighting, backdrops, mounts, and production tools — nothing to source separately.',
    color: 'bg-amber-100 text-amber-600',
  },
  {
    icon: 'ri-time-line',
    title: 'Flexible Booking Options',
    description: 'Book by the hour, half-day, or full day. No long-term leases, no hidden fees — pay only for exactly the time you need.',
    color: 'bg-emerald-100 text-emerald-600',
  },
  {
    icon: 'ri-sparkling-line',
    title: 'Clean, Professional Environments',
    description: 'Every space is cleaned and reset between bookings. You\'ll always walk into a pristine, professionally maintained studio.',
    color: 'bg-rose-100 text-rose-600',
  },
  {
    icon: 'ri-focus-3-line',
    title: 'Ideal for Content & Production',
    description: 'Whether you\'re shooting a product campaign, hosting a podcast, or filming a brand video, we have the right space for your project.',
    color: 'bg-violet-100 text-violet-600',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Dedicated Support Team',
    description: 'Our studio coordinators are available before and during your booking to ensure everything goes smoothly and your shoot succeeds.',
    color: 'bg-orange-100 text-orange-600',
  },
  {
    icon: 'ri-map-2-line',
    title: 'Convenient Metro Locations',
    description: 'Studios in the heart of major creative districts — accessible with parking, public transit, and loading docks at every location.',
    color: 'bg-teal-100 text-teal-600',
  },
];

export default function WhyChooseUs({
  features = defaultFeatures,
  title = 'Why Choose Book My Studio Space?',
  subtitle = 'We built the platform we always wished existed — professional studio access without the complexity or commitment.',
}: WhyChooseUsProps) {
  return (
    <section className="py-24 bg-stone-950 text-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-3 block">Why Us</span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-5">{title}</h2>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="group bg-stone-900 hover:bg-stone-800 border border-stone-800 hover:border-amber-900/50 rounded-2xl p-7 transition-all duration-300"
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 ${feature.color}`}>
                <i className={`${feature.icon} text-xl`} />
              </div>
              <h3 className="font-serif text-xl font-bold text-white mb-3">{feature.title}</h3>
              <p className="text-stone-400 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-16 border-t border-stone-800 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: '50+', label: 'Studio Spaces' },
            { value: '6', label: 'Major Markets' },
            { value: '10,000+', label: 'Creators Served' },
            { value: '4.9★', label: 'Average Rating' },
          ].map((stat) => (
            <div key={stat.label}>
              <div className="font-serif text-3xl font-bold text-amber-400 mb-1">{stat.value}</div>
              <div className="text-stone-500 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
